/* React */
import { Component, ReactNode, useEffect, useState } from 'react'

/* Material */
import { Box } from '@mui/system'
import Modal from '@mui/material/Modal'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'

import useStyles from './styles.Modal'

/* Types */
import { Images, imageSrcs } from 'src/shared/frontendTypes/images'

/* Functions */
import getImageUrl from '../../functions/imageUrl'
import getImageAlt from '../../functions/imageAlt'

/* Components */
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import noImage from 'src/images/camera.svg'

/* Swiper */
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, {
  Navigation,
  Zoom,
} from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/zoom'

SwiperCore.use([
  Navigation,
  Zoom,
])

const legacyFrameSx = {
  position: 'relative',
  maxWidth: '95%',
  maxHeight: '95%',
  bgcolor: 'white',
  border: 0,
  outline: 0,
  padding: '15px',
  boxShadow: '0 18px 50px rgba(0, 0, 0, .22)',
}

interface SwiperErrorBoundaryProps {
  fallback: ReactNode
  children: ReactNode
}

class SwiperErrorBoundary extends Component<
  SwiperErrorBoundaryProps,
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) return this.props.fallback

    return this.props.children
  }
}

interface Props {
  images: Images | imageSrcs
  imageIndex: number
  productID: number
  open: boolean
  metaTitle: string
  fullscreen?: boolean
  handleClose: () => void
}

const PreviewModal: React.FC<Props> = ({
  open,
  images,
  imageIndex,
  productID,
  metaTitle,
  fullscreen = false,
  handleClose
}) => {
  const classes = useStyles()
  const modalImages = Array.isArray(images) ? images : []
  const hasInteractiveZoom = fullscreen
  const [swiper, setSwiper] = useState<SwiperCore | null>(null)
  const [activeIndex, setActiveIndex] = useState(imageIndex)
  const [zoomed, setZoomed] = useState(false)
  const currentImage = modalImages[activeIndex] || modalImages[0]
  const fallbackImagePath = currentImage
    ? getImageUrl(currentImage, productID, 'xxl')
    : null
  const fallbackAlt = getImageAlt(currentImage, metaTitle)

  const resetZoom = (nextSwiper?: SwiperCore | null) => {
    if (!hasInteractiveZoom || !nextSwiper?.zoom || nextSwiper.destroyed) return

    try {
      nextSwiper.zoom.out()
    } catch (error) {
      console.warn('Bildzoom konnte nicht zurueckgesetzt werden.', error)
    }
  }

  useEffect(() => {
    if (!open) return

    setActiveIndex(imageIndex)
    setZoomed(false)
    if (swiper && !swiper.destroyed) {
      swiper.slideTo(imageIndex, 0)
      resetZoom(swiper)
    }
  }, [imageIndex, open, swiper])

  const handleSlideChange = (nextSwiper: SwiperCore) => {
    setActiveIndex(nextSwiper.activeIndex)
    setZoomed(false)
    resetZoom(nextSwiper)
  }

  const handleZoomToggle = () => {
    if (!hasInteractiveZoom || !swiper?.zoom || swiper.destroyed) return

    try {
      if (zoomed) {
        swiper.zoom.out()
        setZoomed(false)
      } else {
        swiper.zoom.in()
        setZoomed(true)
      }
    } catch (error) {
      console.warn('Bildzoom konnte nicht geaendert werden.', error)
      setZoomed(false)
    }
  }

  return <Modal
    className={classes.modalRoot}
    aria-label="Produktbilder"
    open={open}
    onClose={handleClose}
  >
    <Box
      className={fullscreen ? classes.fullscreenModal : classes.legacyModal}
      sx={fullscreen ? undefined : legacyFrameSx}
    >
      {fullscreen
        ? <div className={classes.fullscreenToolbar}>
          <div className={classes.imageCounter} aria-live='polite'>
            {activeIndex + 1} / {modalImages.length}
          </div>
          <div className={classes.fullscreenControls}>
            <IconButton
              className={classes.fullscreenIconButton}
              aria-label={zoomed ? 'Bild verkleinern' : 'Bild vergroessern'}
              onClick={handleZoomToggle}
              disabled={!swiper || swiper.destroyed}
              size='large'
            >
              {zoomed ? <ZoomOutIcon fontSize='large' /> : <ZoomInIcon fontSize='large' />}
            </IconButton>
            <IconButton
              className={classes.fullscreenIconButton}
              aria-label='Bildansicht schliessen'
              onClick={handleClose}
              size='large'
            >
              <CloseIcon fontSize='large' />
            </IconButton>
          </div>
        </div>
        : <IconButton
          className={classes.closeButton}
          size='large'
          onClick={handleClose}
          aria-label='Bildansicht schliessen'
        >
          <CloseIcon fontSize='large' />
        </IconButton>
      }

      <div className={fullscreen ? classes.fullscreenStage : classes.legacyStage}>
        <SwiperErrorBoundary
          key={`${productID}-${open ? imageIndex : 'closed'}-${modalImages.length}`}
          fallback={
            <div className={classes.fallbackImage}>
              <img
                alt={fallbackAlt}
                src={fallbackImagePath || noImage.src}
                loading='lazy'
              />
              <AiGeneratedBadge aiGenerated={currentImage?.aiGenerated} />
            </div>
          }
        >
          <Swiper
            className={fullscreen ? classes.fullscreenImageSwiper : classes.imageSwiper}
            spaceBetween={50}
            slidesPerView={1}
            simulateTouch={fullscreen}
            allowTouchMove
            cssMode={false}
            zoom={hasInteractiveZoom ? { maxRatio: 4, minRatio: 1, toggle: true } : false}
            navigation={modalImages.length > 1}
            initialSlide={imageIndex}
            onSwiper={setSwiper}
            onSlideChange={handleSlideChange}
            onZoomChange={(_, scale) => setZoomed(Number(scale) > 1)}
          >
            {modalImages.map((image, index) => {
              const imagePath = getImageUrl(image, productID, 'xxl')
              const alt = getImageAlt(image, metaTitle)
              const imgRender = <img
                alt={alt}
                src={imagePath || noImage.src}
                loading='lazy'
              />

              return <SwiperSlide key={index} className={classes.imageSlide}>
                <div className='swiper-zoom-container'>
                  {imgRender}
                </div>
                <AiGeneratedBadge aiGenerated={image.aiGenerated} />
              </SwiperSlide>
            })}
          </Swiper>
        </SwiperErrorBoundary>
      </div>

    </Box>
  </Modal>
}

export default PreviewModal
